import { toJpeg } from 'html-to-image';
import JSpdf from 'jspdf';
import moment from 'moment';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import ArrowRight from 'assets/images/check-pdf/arrow-right.png';
import CheckSuccess from 'assets/images/check-pdf/check-success.png';
import Hourglass from 'assets/images/hourglass.svg';
import { ReactComponent as Kassa24Logo } from 'assets/images/kassa-logo-black.svg';
import { companyType, companyTypeLabel } from 'constants/check-view';
import { dateFormatOfd } from 'constants/date';
import { PAY_TYPE, PaymentStatus } from 'constants/payment';
import { useIsMobile, useMainNavigate, useTranslate } from 'hooks';
import { useAppSelector } from 'store/hooks';
import type { CompanyType } from 'types';
import { costFormat, formatDateWithTimezone } from 'utils';
import { normalizeString } from 'utils/normalize-string';
import { SendCheckForCheckview } from 'views/forms/base/send-check-for-checkview';

import { Button } from '../ui';

const BRLine = () => {
  return <div className="bg-base-gray-300 h-[1px] w-full my-3" />;
};

const WITH_FISCAL_DATA = false;

const CheckView = () => {
  const { paymentData, isValidForCheck } = useAppSelector((state) => state.payment);
  const {
    status,
    fullName,
    legalEntityType,
    amount,
    commission,
    fullAmount,
    merchantBIN,
    merchantName,
    date,
    ID,
    description,
    paymentType,
    returnURL,
  } = paymentData ?? {};

  const targetRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useIsMobile();
  const { goToPaymentView } = useMainNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const normalAmount = costFormat(Math.abs(Number(amount)));
  const normalFullAmount = costFormat(Math.abs(Number(fullAmount)));
  const normalCommission = costFormat(Math.abs(Number(commission || 0)));

  const fp = ID?.slice(8, 18);
  const { t } = useTranslate();

  const handleCreatePDF = async () => {
    const element = targetRef.current;
    try {
      if (element) {
        const clonedElement = element.cloneNode(true) as HTMLDivElement;

        clonedElement.classList.add('pdf-styles');
        document.body.appendChild(clonedElement);
        clonedElement.style.position = 'fixed';
        clonedElement.style.top = '-16px';
        clonedElement.style.zIndex = '-10';

        const img = await toJpeg(clonedElement);

        const pdf = new JSpdf('p', 'mm', [297, 210]);

        const imgProperties = pdf.getImageProperties(img);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;
        pdf.addImage(img, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`check_${ID}.pdf`);
        document.body.removeChild(clonedElement);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (!isValidForCheck) {
      goToPaymentView(paymentData?.ID || id || '');
    }
  }, [paymentData, isValidForCheck, goToPaymentView, id]);

  if (status !== PaymentStatus.HOLDED && status !== PaymentStatus.PAYMENT_SUCCESS) {
    return <div />;
  }

  return (
    <div
      ref={targetRef}
      className="pt-8 md:py-8 px-2.5 bg-[#f4f6fc] font-fira-mono relative min-h-screen"
    >
      <div className="CheckView__header max-w-[960px] w-full mx-auto justify-around mb-6 md:flex hidden">
        <a
          className="CheckView__header__back text-sm font-normal text-base-gray-600 flex items-center w-[170px] group gap-2"
          href={returnURL || '/'}
        >
          <i className="ri-arrow-left-line" />
          <span className="group-hover:underline">{t('go_back_website')}</span>
        </a>
        <div className="flex-grow CheckView__header__logo">
          <Kassa24Logo className="h-[21px] mx-auto" />
        </div>
        <div className="w-[170px] CheckView__header__back" />
      </div>
      <div className="CheckView__content md:max-w-[362px] mx-auto">
        <div className="CheckView__content__top p-6 rounded-xsmall shadow-check text-[#33333d] leading-5 bg-white">
          <div>
            {status === PaymentStatus.HOLDED ? (
              <div className="flex items-center gap-2 flex-col text-center font-inter">
                <span>
                  Өңдеуге алынды /
                  <br />
                  Принято в обработку
                </span>
                <img src={Hourglass} alt="hourglass" className="w-6 h-6" />
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-col text-center font-inter">
                <span>
                  Төлем қабылданды /
                  <br />
                  {paymentType === PAY_TYPE.PAYMENT ? 'Платеж принят' : 'Выплата принята'}
                </span>
                <img src={CheckSuccess} alt="success" className="w-6 h-6" />
              </div>
            )}
          </div>
          <BRLine />
          <div className="flex flex-col gap-[10px]">
            <p className="font-bold">
              {legalEntityType
                ? companyTypeLabel[normalizeString(legalEntityType) as CompanyType]
                : ''}{' '}
              “{fullName}”
            </p>
            <p>
              {legalEntityType && normalizeString(legalEntityType) === companyType.SP
                ? 'ЖСН / ИИН'
                : 'БСН / БИН'}
              : {merchantBIN}
            </p>
            <div className="group">
              <p>Чек №{ID}</p>
              <p>{date && formatDateWithTimezone(date)}</p>
              <p>Сатушы / Кассир: {merchantName}</p>
              <p>Түсініктеме / Комментарий: {description}</p>
            </div>
            <p className="font-bold">{paymentType === 0 ? 'Төлеу / Оплата' : 'Төлем / Выплата'}</p>
          </div>
          <BRLine />
          <div className="flex flex-col gap-[10px]">
            <p className="font-bold">
              {paymentType === 0
                ? 'Интернет арқылы төлем / Оплата в интернете'
                : 'Интернет арқылы төлеу / Выплата в интернете'}
            </p>
            <div className="group">
              <p>{normalAmount} x 1</p>
              <div className="flex justify-between items-center">
                <p>Бағасы / Стоимость</p>

                <p>={normalAmount}</p>
              </div>
              <p>ҚҚС-сыз / БЕЗ НДС</p>
            </div>
          </div>
          <BRLine />
          <div className="flex flex-col gap-[10px]">
            <div className="flex justify-between items-center">
              <p className="font-bold text-[18px] whitespace-nowrap">ЖИЫНЫ / ИТОГ</p>
              <p className="font-bold text-[18px]">={normalFullAmount}</p>
            </div>
            <div className="group">
              <div className="flex justify-between items-center">
                <p>Қолма-қол ақшасыз / Безналичными</p>
                <p className="sum shrink-0">={normalAmount}</p>
              </div>
              <div className="flex justify-between items-center">
                <p>Комиссия</p>
                <p className="sum shrink-0">={normalCommission}</p>
              </div>
              <div className="flex justify-between items-center">
                <p>ҚҚС / НДС</p>
                <p>=0.00</p>
              </div>
            </div>
          </div>
          {WITH_FISCAL_DATA && (
            <>
              <BRLine />
              <div className="flex flex-col gap-[10px]">
                <div>
                  <p>БКМ Мекен-жайы / Адрес ККМ:</p>
                  <p>Алматы қ., М. Өзтүрік к-сі, 7 - 315 /</p>
                  <p>г. Алматы, М. Озтюрка 7 - 315</p>
                  <p>МТН / РНМ: 010101040151</p>
                  <p>МЗН / ЗНМ: 530528</p>
                </div>

                <div>
                  <p>Фискалдық белгі / Фискальный признак: {fp}</p>
                  <p>ФДО / ОФД: Транстелеком</p>
                  <p>Сайт: ofd1.kz</p>
                </div>

                <div>
                  <p>
                    ҚР тізіліміне 182 нөмірімен еңгізілді / КС внесена в Госреестр РК под номером
                    182
                  </p>
                </div>
              </div>
              <BRLine />
              <div className="flex items-center gap-[10px]">
                <a
                  href={`https://ofd1.kz/show_ticket?date=${dateFormatOfd(
                    moment(date).toDate(),
                  )}&fiscal_mark=${fp}&state_number=010101040151`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <QRCodeSVG
                    size={isMobile ? 66 : 80}
                    id="ofd1-qr-code"
                    style={{
                      width: isMobile ? '66px' : '80px',
                      height: isMobile ? '66px' : '80px',
                    }}
                    value={`https://ofd1.kz/show_ticket?date=${dateFormatOfd(
                      moment(date).toDate(),
                    )}&fiscal_mark=${fp}&state_number=010101040151`}
                  />
                </a>
                <div className="flex items-center gap-2 text-[11px] md:text-[13px] font-inter text-left whitespace-nowrap">
                  <img src={ArrowRight} alt="arrow right" className="w-4 h-4" />
                  <b>
                    QR арқылы чекті тексеріңіз /
                    <br />
                    Проверьте чек по QR
                  </b>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="CheckView__content__bottom mt-5 md:mt-2.5 flex flex-col gap-3.5">
          <Button
            onClick={handleCreatePDF}
            className="h-8 md:h-9 md:rounded-xsmall font-medium md:py-2 bg-base-yellow hover:bg-base-yellow active:bg-base-yellow text-black"
          >
            {t('download_pdf')} <i className="ri-download-2-line align-middle" />
          </Button>

          <SendCheckForCheckview />
          <div className="h-[54px] md:h-0 mt-6 md:mt-0" />
          <a
            className="CheckView__content__back-btn left-0 bg-black w-full md:hidden block text-white text-center h-[54px] py-4 mt-6 absolute bottom-0"
            href={returnURL || '/'}
          >
            {t('go_back_website')}
          </a>
        </div>
      </div>
    </div>
  );
};

export { CheckView };
